import { UserInformation } from '@rover/rsdk/src/apiClient/latest';
import { IsFlagActive } from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { isMobileBrowser } from '@rover/rsdk/src/modules/Network/userAgent';
import type { DataLayerEvent } from '@rover/types';
import fireDataLayerEvent from '@rover/utilities/fireDataLayerEvent';
import shouldLoadAmplitudeSessionReplay from '@rover/utilities/shouldLoadAmplitudeSessionReplay';
import shouldLoadFullStory from '@rover/utilities/shouldLoadFullStory';

export type PageLoadParams = {
  pageCategory: string;
  currentUser: UserInformation;
  isFlagActive: (string) => boolean;
  pathname: string;
};

export type DataLayerParams = {
  pageCategory: string;
  currentUser: UserInformation;
};

export const fireDataLayerLoaded = async ({
  pageCategory,
  currentUser: { opk, providerGroupSlug },
}: DataLayerParams): Promise<void> => {
  const isAuthed: boolean = !!opk && !!opk.length;

  return fireDataLayerEvent({
    event: 'dataLayer-loaded',
    pageCategory,
    loggedIn: `logged ${isAuthed ? 'in' : 'out'}`,
    ...(isAuthed
      ? {
          personId: opk,
          sitterType: providerGroupSlug,
        }
      : {}),
    deviceType: isMobileBrowser() ? 'mobile' : 'desktop',
  });
};

export const fireLoadFullStoryJs = async (isFlagActive: IsFlagActive): Promise<void> => {
  if (process.env.JS_ENV_CLIENT && shouldLoadFullStory(isFlagActive, window.location.pathname)) {
    await fireDataLayerEvent({
      event: 'load-full-story',
    });
  }
};

// Disable GTM-based amplitude, and remove this lifecycle event in a followup PR
export const fireLoadAmplitudeJs = async (): Promise<void> => {};

export const fireLoadAmplitudeSessionReplayJs = async (
  isFlagActive: IsFlagActive
): Promise<void> => {
  if (
    process.env.JS_ENV_CLIENT &&
    // Do not enable fullstory and amplitude session replay at the same time
    !shouldLoadFullStory(isFlagActive, window.location.pathname) &&
    shouldLoadAmplitudeSessionReplay(isFlagActive, new URL(window.location.href))
  ) {
    await fireDataLayerEvent({
      event: 'load-amplitude-session-replay-js',
    });
  }
};

export const fireLoadImpactJs = async ({
  currentUser: { emailHash = '' },
}: {
  currentUser: UserInformation;
}): Promise<void> =>
  fireDataLayerEvent({
    event: 'load-impact-js',
    emailSha: emailHash,
  });

export const fireRouteChange = async ({ pathname }: { pathname: string }): Promise<void> =>
  fireDataLayerEvent({
    event: 'route-change',
    newRoute: pathname,
  });

export const fireSessionEvents = async (events: DataLayerEvent[]): Promise<void> => {
  if (events?.length) {
    await Promise.all(events.map(fireDataLayerEvent));
  }
};

export default {
  fireDataLayerLoaded,
  fireLoadFullStoryJs,
  fireLoadImpactJs,
  fireLoadAmplitudeJs,
  fireLoadAmplitudeSessionReplayJs,
  fireRouteChange,
  fireSessionEvents,
};
